// @flow
/* eslint-disable import/prefer-default-export */
import { createSelector } from 'reselect';
import idx from 'idx';

import * as cc from 'common/constants';
import * as routeSelectors from 'modules/router/selectors';
import type { AppState } from 'store/types';

import * as c from './constants';
import type { RootState } from './reducers';

const rootModule = (state: AppState): RootState => state[c.MODULE_NAME];

// MAIN FIELDS IN STORE
// ============================================================================

export const isOwnedMode = createSelector([rootModule], (state) => state.isOwnedMode);

export const authOwnAdvertiser = createSelector([rootModule], (state) => state.ownAdvertiser.auth);
export const sofarmOwnAdvertiser = createSelector(
  [rootModule],
  (state) => state.ownAdvertiser.sofarm,
);
export const billingOwnAdvertiser = createSelector(
  [rootModule],
  (state) => state.ownAdvertiser.billing,
);

const authAdvertiser = createSelector(
  [rootModule, isOwnedMode, authOwnAdvertiser],
  (state, isOwned, ownAdvertiser) => (isOwned ? ownAdvertiser : state.advertiser.auth) || {},
);
const sofarmAdvertiser = createSelector(
  [rootModule, isOwnedMode, sofarmOwnAdvertiser],
  (state, isOwned, ownAdvertiser) => (isOwned ? ownAdvertiser : state.advertiser.sofarm) || {},
);
const billingAdvertiser = createSelector(
  [rootModule, isOwnedMode, billingOwnAdvertiser],
  (state, isOwned, ownAdvertiser) => (isOwned ? ownAdvertiser : state.advertiser.billing) || {},
);

// COMPUTED FIELDS
// ============================================================================

export const isLoaded = createSelector(
  [authAdvertiser, sofarmAdvertiser, billingAdvertiser],
  (...advertisers) => advertisers.every((i) => i != null),
);

export const advertiserLoadedId = createSelector(
  [isOwnedMode, authAdvertiser, sofarmAdvertiser],
  (isOwned, auth, sofarm) => (isOwned ? idx(sofarm, (_) => _.id) : idx(auth, (_) => _.id)),
);

export const advertiserOwnId = createSelector([sofarmOwnAdvertiser], (advertiser) =>
  idx(advertiser, (_) => _.id),
);

export const advertiserId = createSelector(
  [advertiserLoadedId, routeSelectors.payload],
  (id, routePayload) => {
    if (id != null) {
      return id;
    }

    return routePayload ? parseInt(routePayload.id, 10) : null;
  },
);
export const advertiserScreenName = createSelector(
  [sofarmAdvertiser],
  (advertiser) => advertiser.screenName,
);
export const advertiserName = createSelector([sofarmAdvertiser], (advertiser) => advertiser.name);
export const advertiserVip = createSelector([sofarmAdvertiser], (advertiser) => advertiser.vip);
export const advertiserType = createSelector([sofarmAdvertiser], (advertiser) => advertiser.type);
export const advertiserEmail = createSelector([sofarmAdvertiser], (advertiser) => advertiser.email);
export const advertiserManagers = createSelector(
  [sofarmAdvertiser],
  (advertiser) => advertiser.managers || [],
);
export const advertiserBalance = createSelector([billingAdvertiser], (advertiser) =>
  idx(advertiser, (_) => _.balance.balance),
);
export const advertiserAvailableBalance = createSelector([billingAdvertiser], (advertiser) =>
  idx(advertiser, (_) => _.balance.availableBalance),
);
export const advertiserOverdraft = createSelector([billingAdvertiser], (advertiser) =>
  idx(advertiser, (_) => _.balance.allowedOverdraft),
);
// FIXME: get it from "modules/voucher/selectors" (without circular deps with modules/auth/selectors)
const FIXME_activeVouhcer = (state) => state.voucher.active;
export const advertiserCurrentVoucher = createSelector(
  [isOwnedMode, billingAdvertiser, FIXME_activeVouhcer],
  (isOwned, advertiser, activeVoucher) => (isOwned ? activeVoucher : advertiser.currentVoucher),
);
export const advertiserBillingInfo = createSelector(
  [billingAdvertiser],
  (advertiser) => advertiser.billingInfo,
);
export const advertiserAdditionalCardsLimit = createSelector(
  [sofarmAdvertiser],
  (advertiser) => advertiser.additionalCardsLimit,
);
export const advertiserPaymentTopUpFee = createSelector(
  [sofarmAdvertiser],
  (advertiser) => advertiser.paymentSourceTopUpFee,
);
export const advertiserPaymentTopUpMaxAmount = createSelector(
  [sofarmAdvertiser],
  (advertiser) => advertiser.paymentSourceTopUpMaxAmount,
);
export const advertiserPaymentTopupLimit = createSelector(
  [sofarmAdvertiser],
  (advertiser) => advertiser.cardTopupLimit,
);

// FIXME: get it from "modules/auth/selectors" (without circular deps with modules/auth/selectors)
export const FIXME_authUserScopes = (state) => idx(state, (_) => _.auth.token.scopes);
export const advertiserIsBlackHat = createSelector(
  [isOwnedMode, authAdvertiser, FIXME_authUserScopes],
  (isOwned, advertiser, authScopes) =>
    ((isOwnedMode ? authScopes : advertiser.scopes) || []).includes(cc.SCOPE.BLACK_HAT),
);



// WEBPACK FOOTER //
// ./src/modules/advertiser/selectors.js